import { useContext, useRef } from "react";
import { AbortError, ValidationResult } from "sprint-common-web";

import { AppConfigContext } from "../../../core/configuration/Configuration";
import { useAuthorizedFetch } from "../../../core/data-access/useAuthorizedFetch";
import { cacheRequests } from "../../utils/cacheRequests";
import { CreateEntityResponse } from "../common/CreateEntityResponse";
import { BucketInfoResponse } from "./BucketInfoResponse";
import { CreateBucketArchiveRequest } from "./CreateBucketArchiveRequest";
import { CreateBucketDeliveryRequest } from "./CreateBucketDeliveryRequest";
import { CreateDeliveryRequest } from "./CreateDeliveryRequest";
import { CreateKeyBasedDeliveryBucketRequest } from "./CreateKeyBasedDeliveryBucketRequest";
import { CreateKeyBasedDeliveryBucketResponse } from "./CreateKeyBasedDeliveryBucketResponse";
import { CreateRoleBasedDeliveryBucketRequest } from "./CreateRoleBasedDeliveryBucketRequest";
import { CreateRoleBasedDeliveryBucketResponse } from "./CreateRoleBasedDeliveryBucketResponse";
import { DeleteDeliveryBucketRequest } from "./DeleteDeliveryBucketRequest";
import { DeliveryTypesResponse } from "./DeliveryTypesResponse";
import { DisableDeliveryBucketRequest } from "./DisableDeliveryBucketRequest";
import { EnableDeliveryBucketRequest } from "./EnableDeliveryBucketRequest";
import { GetArchiveBucketFilesQuery } from "./GetArchiveBucketFilesQuery";
import { GetBucketArchiveListResponse } from "./GetBucketArchiveListResponse";
import { GetDeliveriesBucketFilesQuery } from "./GetDeliveriesBucketFilesQuery";
import { GetDeliveryBucketContentRequest } from "./GetDeliveryBucketContentRequest";
import { GetDeliveryBucketListResponse } from "./GetDeliveryBucketListResponse";
import { GetDeliveryResponse } from "./GetDeliveryResponse";
import { GetUploadBucketFilesQuery } from "./GetUploadBucketFilesQuery";
import { ImportBucketArchiveRequest } from "./ImportBucketArchiveRequest";
import { CreateOriginatorRequest } from "./originators/CreateOriginatorRequest";
import { GetOriginatorsListResponse } from "./originators/GetOriginatorsListResponse";
import { GetOriginatorsLookupResponse } from "./originators/GetOriginatorsLookupResponse";
import { SearchBucketArchiveQuery } from "./SearchBucketArchiveQuery";
import { SearchDeliveriesBucketQuery } from "./SearchDeliveriesBucketQuery";
import { SearchUploadBucketQuery } from "./SearchUploadBucketQuery";
import { UpdateDeliveryRequest } from "./UpdateDeliveryRequest";

const originatorsCache = cacheRequests<GetOriginatorsLookupResponse>();

export const useDeliveryApi = () => {
    const appConfigContext = useContext(AppConfigContext);
    const { httpGet, httpPost, httpPut, httpDelete } = useAuthorizedFetch();
    const baseDeliveryUrl = appConfigContext.deliveryConfig.apiUrl;

    const getUploadBucketFilesAbortController = useRef<AbortController>(new AbortController());

    const getArchiveBucketFilesAbortController = useRef<AbortController>(new AbortController());

    const getDeliveriesBucketFilesAbortController = useRef<AbortController>(new AbortController());

    const createDelivery = async (request: CreateDeliveryRequest) => {
        return httpPost(`${baseDeliveryUrl}/deliveries`, request);
    };

    const getDelivery = async (deliveryId: string) => {
        return httpGet<GetDeliveryResponse>(`${baseDeliveryUrl}/deliveries/${deliveryId}`);
    };

    const updateDelivery = async (deliveryId: string, request: UpdateDeliveryRequest) => {
        return httpPut(`${baseDeliveryUrl}/deliveries/${deliveryId}`, request);
    };

    const getOriginatorsLookup = async (): Promise<GetOriginatorsLookupResponse> =>
        originatorsCache.getRequest({
            requestFn: () => httpGet<GetOriginatorsLookupResponse>(`${baseDeliveryUrl}/originators/get-lookup`),
            key: "originators",
            ttlMs: 20000
        });

    const getOriginatorsList = async (): Promise<GetOriginatorsListResponse> => {
        return httpGet<GetOriginatorsListResponse>(`${baseDeliveryUrl}/originators/get-list`);
    };

    const createOriginator = async (request: CreateOriginatorRequest): Promise<CreateEntityResponse> => {
        return httpPost(`${baseDeliveryUrl}/originators/create`, request);
    };

    const getDeliveryTypes = async (): Promise<DeliveryTypesResponse> => {
        return httpGet<DeliveryTypesResponse>(`${baseDeliveryUrl}/delivery-types`);
    };

    const createBucketDelivery = async (request: CreateBucketDeliveryRequest): Promise<CreateEntityResponse> => {
        return httpPost(`${baseDeliveryUrl}/bucket-deliveries/create`, request);
    };

    const getDeliveryBucketContent = async (request: GetDeliveryBucketContentRequest): Promise<BucketInfoResponse> => {
        return httpGet<BucketInfoResponse>(`${baseDeliveryUrl}/buckets/get-content`, request);
    };

    const createKeyBasedDeliveryBucket = async (
        request: CreateKeyBasedDeliveryBucketRequest
    ): Promise<CreateKeyBasedDeliveryBucketResponse> => {
        return httpPost<CreateKeyBasedDeliveryBucketResponse>(`${baseDeliveryUrl}/buckets/create-key-based`, request);
    };

    const createRoleBasedDeliveryBucket = async (
        request: CreateRoleBasedDeliveryBucketRequest
    ): Promise<CreateRoleBasedDeliveryBucketResponse> => {
        return httpPost<CreateRoleBasedDeliveryBucketResponse>(`${baseDeliveryUrl}/buckets/create-role-based`, request);
    };

    const deleteDeliveryBucket = async (request: DeleteDeliveryBucketRequest) => {
        return httpDelete(`${baseDeliveryUrl}/buckets/delete`, request);
    };

    const validateUniqueId = async (uniqueId: string, signal?: AbortSignal): Promise<ValidationResult> => {
        return httpGet<ValidationResult>(`${baseDeliveryUrl}/buckets/validate-unique-id`, { uniqueId }, signal);
    };

    const validateBucketArchiveUniqueId = async (uniqueId: string, signal?: AbortSignal): Promise<ValidationResult> => {
        return httpGet<ValidationResult>(`${baseDeliveryUrl}/archive-bucket/validate-unique-id`, { uniqueId }, signal);
    };

    const getDeliveryBucketList = async (): Promise<GetDeliveryBucketListResponse> => {
        return httpGet<GetDeliveryBucketListResponse>(`${baseDeliveryUrl}/buckets/list`);
    };

    const disableDeliveryBucket = async (request: DisableDeliveryBucketRequest) => {
        return httpPut(`${baseDeliveryUrl}/buckets/disable`, request);
    };

    const enableDeliveryBucket = async (request: EnableDeliveryBucketRequest) => {
        return httpPut(`${baseDeliveryUrl}/buckets/enable`, request);
    };

    const inviteToDeliveryBucket = async (request: {
        deliveryBucketId: string;
        body: string;
        subject: string;
        recipientEmail: string;
    }): Promise<void> => {
        return httpPost(`${baseDeliveryUrl}/buckets/invite`, request);
    };

    const createArchiveBucket = async (request: CreateBucketArchiveRequest): Promise<CreateEntityResponse> => {
        return httpPost(`${baseDeliveryUrl}/archive-bucket/create`, request);
    };

    const getArchiveBucketList = async (): Promise<GetBucketArchiveListResponse> => {
        return httpGet<GetBucketArchiveListResponse>(`${baseDeliveryUrl}/archive-bucket/list`);
    };

    const importBucketArchive = async (request: ImportBucketArchiveRequest): Promise<CreateEntityResponse> => {
        return httpPost(`${baseDeliveryUrl}/archive-bucket/import`, request);
    };

    const getArchiveBucketFiles = async (query?: GetArchiveBucketFilesQuery): Promise<BucketInfoResponse | void> => {
        if (getArchiveBucketFilesAbortController.current.signal.aborted)
            getArchiveBucketFilesAbortController.current = new AbortController();

        try {
            return await httpGet<BucketInfoResponse>(
                `${baseDeliveryUrl}/archive-bucket/files`,
                query,
                getArchiveBucketFilesAbortController.current.signal
            );
        } catch (err) {
            handleAbortError(err);

            return;
        }
    };

    const abortGetArchiveBucketFilesRequest = () => abortRequest(getArchiveBucketFilesAbortController);

    const searchArchiveBucket = async (query: SearchBucketArchiveQuery): Promise<BucketInfoResponse> => {
        return httpGet<BucketInfoResponse>(`${baseDeliveryUrl}/archive-bucket/search`, query);
    };

    const getUploadBucketFiles = async (query?: GetUploadBucketFilesQuery): Promise<BucketInfoResponse | void> => {
        if (getUploadBucketFilesAbortController.current.signal.aborted)
            getUploadBucketFilesAbortController.current = new AbortController();

        try {
            return await httpGet<BucketInfoResponse>(
                `${baseDeliveryUrl}/upload-bucket/files`,
                query,
                getUploadBucketFilesAbortController.current.signal
            );
        } catch (err) {
            handleAbortError(err);

            return;
        }
    };

    const abortGetUploadBucketFilesRequest = () => abortRequest(getUploadBucketFilesAbortController);

    const searchUploadBucket = async (query: SearchUploadBucketQuery): Promise<BucketInfoResponse> => {
        return await httpGet<BucketInfoResponse>(`${baseDeliveryUrl}/upload-bucket/search`, query);
    };

    const getDeliveriesBucketFiles = async (
        query?: GetDeliveriesBucketFilesQuery
    ): Promise<BucketInfoResponse | void> => {
        if (getDeliveriesBucketFilesAbortController.current.signal.aborted)
            getDeliveriesBucketFilesAbortController.current = new AbortController();

        try {
            return await httpGet<BucketInfoResponse>(
                `${baseDeliveryUrl}/bucket-deliveries/files`,
                query,
                getDeliveriesBucketFilesAbortController.current.signal
            );
        } catch (err) {
            handleAbortError(err);

            return;
        }
    };

    const abortGetDeliveriesBucketFilesRequest = () => abortRequest(getDeliveriesBucketFilesAbortController);

    const searchDeliveriesBucket = async (query: SearchDeliveriesBucketQuery): Promise<BucketInfoResponse> => {
        return httpGet<BucketInfoResponse>(`${baseDeliveryUrl}/bucket-deliveries/search`, query);
    };

    return {
        createDelivery,
        getDeliveryTypes,
        getDelivery,
        updateDelivery,
        createBucketDelivery,
        createKeyBasedDeliveryBucket,
        createRoleBasedDeliveryBucket,
        getDeliveryBucketContent,
        deleteDeliveryBucket,
        validateUniqueId,
        getDeliveryBucketList,
        disableDeliveryBucket,
        enableDeliveryBucket,
        inviteToDeliveryBucket,
        getOriginatorsLookup,
        getOriginatorsList,
        createOriginator,
        createArchiveBucket,
        validateBucketArchiveUniqueId,
        getArchiveBucketList,
        searchUploadBucket,
        getArchiveBucketFiles,
        abortGetArchiveBucketFilesRequest,
        searchArchiveBucket,
        getUploadBucketFiles,
        abortGetUploadBucketFilesRequest,
        importBucketArchive,
        getDeliveriesBucketFiles,
        abortGetDeliveriesBucketFilesRequest,
        searchDeliveriesBucket
    };
};

const abortRequest = (controllerRef: React.MutableRefObject<AbortController>) => {
    /* do not pass `reason` arg to abort() - it prevents `error.name` property, which we use for error handling under the hood */
    controllerRef.current.abort();
};

const handleAbortError = (err: unknown) => {
    if (err instanceof AbortError) return;

    throw err;
};
